








// Import vendors ----------------------------------------------------------------------------------
import { defineComponent, computed, PropType } from '@vue/composition-api';
// Import components -------------------------------------------------------------------------------
import ChartEvolutionContainer from '@/components/charts/ChartEvolutionContainer.vue';
// Import plugins ----------------------------------------------------------------------------------
import i18n from '@/plugins/i18n';
// Import utils - ----------------------------------------------------------------------------------
import { useChart, EThemeColor } from '@/utils/charts.utils';
import { EUnitKey } from '@/utils/parameter-units.utils';
// Import types ------------------------------------------------------------------------------------
import type { TDataStandard } from '@/components/patient/PatientEvolution.vue';
// -------------------------------------------------------------------------------------------------

export default defineComponent({
  name: 'ChartEvolutionHeelOff',
  components: {
    ChartEvolutionContainer
  },
  props: {
    manifest: {
      type: Array,
      required: true
    },
    analysisType: {
      type: String as PropType<'walk' | 'run'>,
      default: 'walk'
    },
    heelOff: {
      type: Object
    },
    norms: {
      type: Object
    }
  },
  setup(properties) {
    const {
      evolutionChartStructure,
      getSideData,
      getSideDataVariations,
      getSerieItemStructure,
      getNormsStructure
    } = useChart();

    const title = computed(() => `commons.standards.${properties.analysisType}.heel-off`);

    const heelOff: TDataStandard = computed(() => getSideData(properties.heelOff));

    const dataVariations = computed(() =>
      getSideDataVariations({ left: heelOff.value?.left.values, right: heelOff.value?.right.values })
    );

    const valid = computed(() => !!heelOff.value);

    const series = computed(() => {
      let data = [
        // Left
        getSerieItemStructure(
          'heel_off_left',
          `${i18n.t(`commons.standards.${properties.analysisType}.heel-off`)} (${(
            i18n.t('commons.standards.left') as string
          ).slice(0, 1)})`,
          heelOff.value?.left.values,
          EThemeColor.Chart1
        ),
        getSerieItemStructure(
          'heel_off_left',
          undefined,
          heelOff.value?.left.ranges,
          EThemeColor.Chart1,
          true
        ),
        // Right
        getSerieItemStructure(
          'heel_off_right',
          `${i18n.t(`commons.standards.${properties.analysisType}.heel-off`)} (${(
            i18n.t('commons.standards.right') as string
          ).slice(0, 1)})`,
          heelOff.value?.right.values,
          EThemeColor.Chart2
        ),
        getSerieItemStructure(
          'heel_off_right',
          undefined,
          heelOff.value?.right.ranges,
          EThemeColor.Chart2,
          true
        )
      ];
      // Have norms
      if (properties.norms)
        data.push(getNormsStructure('heel_off_norms', properties.norms as { min: number[]; max: number[] }));

      return data;
    });

    const chartOptions = computed(() => {
      return {
        ...evolutionChartStructure(title, properties.manifest, EUnitKey.Degree, 1),
        series: series.value
      };
    });

    return {
      title,
      dataVariations,
      chartOptions,
      valid
    };
  }
});
